import React, { useState, useEffect } from "react";
import { Search, Calendar, Globe, X, Edit3, Check, PlayCircle, AlertTriangle, ChevronRight, Download } from "lucide-react";
import { getAuthHeaders } from "../../auth/services/getAuthHeaders";
import { Spinner } from "../../auth/components/Shared/Icons";
import { useUiLang } from "../../../context/UiLangContext";
import type { TranscriptItem } from "../hooks/useTranslator";

interface Meeting {
  _id: string | number;
  title: string;
  source_lang: string;
  target_lang: string;
  user_id: string;
  created_at: string;
}

interface HistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  translatorServiceUrl: string;
  onLoadMeeting: (
    meetingId: string,
    transcript: TranscriptItem[],
    srcLang: string,
    tgtLang: string
  ) => void;
  recording: boolean;
}

export function HistoryModal({
  isOpen,
  onClose,
  translatorServiceUrl,
  onLoadMeeting,
  recording,
}: HistoryModalProps) {
  const { uiLang } = useUiLang();
  const isEn = uiLang === "en";

  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMeetingId, setLoadingMeetingId] = useState<string | number | null>(null);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [editTitleInput, setEditTitleInput] = useState("");
  const [renaming, setRenaming] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchMeetings();
    } else {
      setMeetings([]);
      setSearchQuery("");
      setError("");
      setEditingId(null);
      setLoadingMeetingId(null);
    }
  }, [isOpen]);

  const fetchMeetings = async (query = "") => {
    setLoading(true);
    setError("");
    try {
      const authHeaders = await getAuthHeaders();
      const cleanedUrl = translatorServiceUrl.replace(/\/$/, "");
      const searchParam = query ? `?search=${encodeURIComponent(query)}` : "";
      
      const res = await fetch(`${cleanedUrl}/meetings${searchParam}`, {
        method: "GET",
        headers: authHeaders,
      });

      if (!res.ok) {
        throw new Error(
          isEn
            ? `Server returned ${res.status} ${res.statusText}`
            : `El servidor devolvió ${res.status} ${res.statusText}`
        );
      }

      const data = await res.json();
      setMeetings(data.meetings || []);
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isEn ? "Failed to retrieve meeting list." : "Error al obtener la lista de reuniones."));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchMeetings(searchQuery);
  };

  const startRename = (meeting: Meeting) => {
    setEditingId(meeting._id);
    setEditTitleInput(meeting.title);
  };

  const handleRenameSubmit = async (meetingId: string | number) => {
    if (!editTitleInput.trim()) return;
    setRenaming(true);
    setError("");

    try {
      const authHeaders = await getAuthHeaders();
      const cleanedUrl = translatorServiceUrl.replace(/\/$/, "");
      const res = await fetch(`${cleanedUrl}/meetings/${meetingId}`, {
        method: "PUT",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ title: editTitleInput.trim() }),
      });

      if (!res.ok) {
        throw new Error(
          isEn
            ? `Rename failed: ${res.status} ${res.statusText}`
            : `Error al renombrar: ${res.status} ${res.statusText}`
        );
      }

      setMeetings((prev) =>
        prev.map((m) => (m._id === meetingId ? { ...m, title: editTitleInput.trim() } : m))
      );
      setEditingId(null);
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isEn ? "Error renaming meeting." : "Error al renombrar la reunión."));
    } finally {
      setRenaming(false);
    }
  };

  const handleLoadMeeting = async (meeting: Meeting) => {
    if (recording) {
      alert(
        isEn
          ? "Please stop the active recording before loading a past meeting."
          : "Por favor, detén la grabación activa antes de cargar una reunión anterior."
      );
      return;
    }

    setLoadingMeetingId(meeting._id);
    setError("");

    try {
      const authHeaders = await getAuthHeaders();
      const cleanedUrl = translatorServiceUrl.replace(/\/$/, "");
      const res = await fetch(`${cleanedUrl}/meetings/${meeting._id}/transcripts`, {
        method: "GET",
        headers: authHeaders,
      });

      if (!res.ok) {
        throw new Error(
          isEn
            ? `Failed to load transcripts: ${res.status} ${res.statusText}`
            : `Error al cargar transcripciones: ${res.status} ${res.statusText}`
        );
      }

      const data = await res.json();
      const rawTranscripts = data.transcripts || [];

      // Map backend fields (original_text, translated_text) to local fields (orig, tran)
      const mappedTranscripts: TranscriptItem[] = rawTranscripts.map((t: any) => {
        const textOrig = t.original_text || "";
        const isMic = textOrig.startsWith("🎤 Yo:");
        return {
          timestamp: t.timestamp || "",
          orig: textOrig,
          tran: t.translated_text || "",
          isMic,
        };
      });

      onLoadMeeting(
        String(meeting._id),
        mappedTranscripts,
        meeting.source_lang,
        meeting.target_lang
      );

      onClose();
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isEn ? "Error loading transcripts." : "Error al cargar las transcripciones de la reunión."));
    } finally {
      setLoadingMeetingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      if (!dateStr) return "";
      let formattedStr = dateStr;
      // If there's a space instead of T (e.g. "YYYY-MM-DD HH:MM:SS"), replace it
      if (formattedStr.includes(" ") && !formattedStr.includes("T")) {
        formattedStr = formattedStr.replace(" ", "T");
      }
      // If it doesn't end with Z or a timezone offset like +HH:MM / -HH:MM, treat it as UTC by appending Z
      if (!formattedStr.endsWith("Z") && !/[+-]\d{2}:?\d{2}$/.test(formattedStr)) {
        formattedStr += "Z";
      }

      const d = new Date(formattedStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString(isEn ? "en-US" : "es-ES", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  // Client-side instant filtering based on searchQuery
  const displayedMeetings = meetings.filter((meeting) => {
    if (!searchQuery.trim()) return true;
    return (meeting.title || "")
      .toLowerCase()
      .includes(searchQuery.trim().toLowerCase());
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
      <div
        className="bg-[var(--window-bg)] border border-[var(--border)] rounded-xl max-w-2xl w-full shadow-2xl overflow-hidden animate-[modal-appear_0.2s_cubic-bezier(0.16,1,0.3,1)] text-[var(--text)] flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-[var(--surface)] border-b border-[var(--border)] flex-shrink-0">
          <div className="flex items-center gap-2">
            <Calendar size={18} className="text-[var(--accent)]" />
            <h2 className="text-sm font-semibold text-[var(--text)] uppercase tracking-wider font-mono">
              {isEn ? "Recording History" : "Historial de grabaciones"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-6 h-6 rounded-md bg-[var(--surface)] hover:bg-[var(--surface-hover)] border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)] flex items-center justify-center transition-all"
            aria-label={isEn ? "Close" : "Cerrar"}
          >
            <X size={14} />
          </button>
        </div>

        {/* Search bar */}
        <div className="px-6 pt-5 pb-3 flex-shrink-0">
          <form onSubmit={handleSearchSubmit} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-dim)]" size={14} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={isEn ? "Search by title..." : "Buscar por título..."}
                className="w-full bg-[var(--surface)] border border-[var(--border)] rounded-lg py-2 pl-9 pr-3 text-xs text-[var(--text)] placeholder-[var(--text-dim)] outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20 transition-all"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-xs font-semibold text-white bg-[var(--accent)] hover:bg-[#006ee6] disabled:opacity-50 active:scale-95 rounded-lg shadow-md transition-all whitespace-nowrap"
            >
              {isEn ? "Search" : "Buscar"}
            </button>
          </form>

          {error && (
            <div className="flex items-start gap-2.5 px-4 py-3 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-xl text-xs font-semibold mt-3 leading-normal">
              <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* List content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-3 min-h-0">
          {loading && meetings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Spinner sizeClass="w-8 h-8" borderClass="border-4 border-[var(--accent)]/20 border-t-[var(--accent)]" />
              <p className="text-xs text-[var(--text-muted)] font-mono animate-pulse">
                {isEn ? "Retrieving history..." : "Obteniendo grabaciones..."}
              </p>
            </div>
          ) : displayedMeetings.length === 0 ? (
            <div className="text-center py-16 text-xs text-[var(--text-dim)] border border-dashed border-[var(--border)] rounded-xl bg-black/5 dark:bg-white/[0.01]">
              {isEn ? "No past meetings found." : "No se encontraron reuniones guardadas."}
            </div>
          ) : (
            displayedMeetings.map((meeting) => (
              <div
                key={meeting._id}
                className="group border border-[var(--border)] bg-[var(--surface)] hover:bg-white/[0.03] dark:hover:bg-white/[0.01] p-4 rounded-xl flex items-center justify-between gap-4 transition-all duration-200"
              >
                <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                  {/* Title editor */}
                  {editingId === meeting._id ? (
                    <div className="flex gap-2 items-center">
                      <input
                        type="text"
                        value={editTitleInput}
                        onChange={(e) => setEditTitleInput(e.target.value)}
                        disabled={renaming}
                        className="bg-[var(--surface)] border border-[var(--border)] rounded-md py-1.5 px-2.5 text-xs text-[var(--text)] font-medium outline-none focus:border-[var(--accent)]"
                        autoFocus
                      />
                      <button
                        onClick={() => handleRenameSubmit(meeting._id)}
                        disabled={renaming}
                        className="p-1.5 rounded-md bg-[var(--accent)] hover:bg-[#006ee6] disabled:opacity-50 text-white flex items-center justify-center transition-all active:scale-95"
                        title={isEn ? "Save" : "Guardar"}
                      >
                        {renaming ? <Spinner sizeClass="w-3 h-3" /> : <Check size={12} />}
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        disabled={renaming}
                        className="p-1.5 rounded-md border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)] hover:bg-[var(--surface-hover)] flex items-center justify-center transition-all"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <h3 className="text-xs font-semibold text-[var(--text)] truncate font-mono">
                        {meeting.title || (isEn ? "Untitled Meeting" : "Reunión sin título")}
                      </h3>
                      <button
                        onClick={() => startRename(meeting)}
                        className="p-1 rounded opacity-0 group-hover:opacity-100 text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--surface-hover)] transition-all flex items-center justify-center"
                        title={isEn ? "Rename" : "Renombrar"}
                      >
                        <Edit3 size={11} />
                      </button>
                    </div>
                  )}

                  {/* Metadata */}
                  <div className="flex flex-wrap items-center gap-y-1 gap-x-4 text-[10px] text-[var(--text-dim)] font-medium">
                    <span className="flex items-center gap-1.5 whitespace-nowrap">
                      <Calendar size={11} />
                      {formatDate(meeting.created_at)}
                    </span>
                    <span className="flex items-center gap-1 bg-black/10 dark:bg-white/[0.05] border border-[var(--border)] px-1.5 py-0.5 rounded font-mono text-[9px]">
                      <Globe size={10} />
                      {meeting.source_lang === "auto" ? "auto" : meeting.source_lang.toUpperCase()}
                      {" → "}
                      {meeting.target_lang.toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <button
                  onClick={() => handleLoadMeeting(meeting)}
                  disabled={loadingMeetingId !== null && loadingMeetingId !== meeting._id}
                  className="inline-flex items-center gap-1.5 py-2 px-3 text-xs font-medium bg-[var(--accent-muted)] hover:bg-[var(--accent)] hover:text-white text-[var(--accent)] rounded-lg transition-all active:scale-95 disabled:opacity-50 whitespace-nowrap"
                >
                  {loadingMeetingId === meeting._id ? (
                    <>
                      <Spinner borderClass="border-2 border-[var(--accent)]/20 border-t-[var(--accent)]" />
                      <span>{isEn ? "Loading..." : "Cargando..."}</span>
                    </>
                  ) : (
                    <>
                      <PlayCircle size={14} />
                      <span>{isEn ? "Load Session" : "Cargar Sesión"}</span>
                    </>
                  )}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
